import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { db } from '../../firebase/config';
import {
    collection, query, where, orderBy, onSnapshot,
    doc, updateDoc, deleteDoc, increment, addDoc, serverTimestamp, setDoc, getDoc, getDocs
} from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorUtils';
import './AdminChats.css';

function AdminChats() {
    const { currentUser }  = useAuth();
    const location         = useLocation();

    const openChatUserId   = location.state?.openChatUserId   || null;
    const openChatUserName = location.state?.openChatUserName || null;

    const [chats,        setChats]        = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages,     setMessages]     = useState([]);
    const [searchQuery,  setSearchQuery]  = useState('');
    const [messageText,  setMessageText]  = useState('');
    const [loading,      setLoading]      = useState(true);
    const [sending,      setSending]      = useState(false);
    const [showOptions,  setShowOptions]  = useState(false);
    const [transientChat, setTransientChat] = useState(null);
    const [activeTab,    setActiveTab]    = useState('active');
    const [actionError,  setActionError]  = useState(null);

    const messagesEndRef = useRef(null);

    // ── Auto-scroll to bottom on new messages ────────────────────────────────
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // ── Fetch all active chats ───────────────────────────────────────────────
    useEffect(() => {
        const chatsQuery = query(
            collection(db, 'chats'),
            where('status', '==', activeTab),
            orderBy('lastMessageTime', 'desc')
        );

        const unsubscribe = onSnapshot(
            chatsQuery,
            (snapshot) => {
                const chatsData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                setChats(chatsData);
                setLoading(false);
            },
            (error) => {
                setActionError(getErrorMessage(error, 'Failed to load chats.'));
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [activeTab]);

    // ── Auto-select chat when navigated from AdminOrderDetails ───────────────
    // If the customer has no existing chat yet, keep a temporary chat in-memory
    // so admin can still start the conversation from Order Details.
    useEffect(() => {
        if (!openChatUserId || loading) return;

        const existing = chats.find(c => c.id === openChatUserId || c.userId === openChatUserId);

        if (existing) {
            setTransientChat(null);
            setSelectedChat(existing);
        } else {
            const tempChat = {
                id: openChatUserId,
                userId: openChatUserId,
                userName: openChatUserName || 'Customer',
                userEmail: '',
                lastMessage: '',
                unreadByAdmin: 0,
                __transient: true,
            };
            setTransientChat(tempChat);
            setSelectedChat(tempChat);
        }
    }, [openChatUserId, openChatUserName, loading, chats]);

    // Keep selected chat in sync and automatically mark as read if currently viewed
    useEffect(() => {
        if (!selectedChat) return;
        const existing = chats.find(c => c.id === selectedChat.id || c.userId === selectedChat.userId);
        if (existing) {
            if (selectedChat.__transient) {
                setSelectedChat(existing);
                setTransientChat(null);
            }
            // If chat is open, immediately clear unread badge in Firestore
            if (existing.unreadByAdmin > 0) {
                updateDoc(doc(db, 'chats', existing.id), { unreadByAdmin: 0 }).catch(console.error);
            }
        }
    }, [chats, selectedChat, selectedChat?.id, selectedChat?.__transient, selectedChat?.userId]);

    // ── Fetch messages for selected chat ─────────────────────────────────────
    useEffect(() => {
        if (!selectedChat) return;

        const messagesQuery = query(
            collection(db, 'chats', selectedChat.id, 'messages'),
            orderBy('timestamp', 'asc')
        );

        const unsubscribe = onSnapshot(
            messagesQuery,
            (snapshot) => {
                const messagesData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                setMessages(messagesData);

                // Mark new customer messages as read by admin
                snapshot.docChanges().forEach(async (change) => {
                    if (change.type === 'added' && !change.doc.data().readByAdmin) {
                        await updateDoc(
                            doc(db, 'chats', selectedChat.id, 'messages', change.doc.id),
                            { readByAdmin: true }
                        );
                    }
                });

                // Reset unread count for admin
                if (selectedChat.unreadByAdmin > 0) {
                    updateDoc(doc(db, 'chats', selectedChat.id), { unreadByAdmin: 0 });
                }
            },
            (error) => {
                setActionError(getErrorMessage(error, 'Failed to load messages.'));
            }
        );

        return () => unsubscribe();
    }, [selectedChat]);

    const handleSelectChat = (chat) => {
        setSelectedChat(chat);
        setShowOptions(false);
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!messageText.trim() || !selectedChat) return;

        setSending(true);
        try {
            const chatRef = doc(db, 'chats', selectedChat.id);
            const chatSnap = await getDoc(chatRef);

            // Persist chat only when the admin sends the first message.
            if (!chatSnap.exists()) {
                await setDoc(chatRef, {
                    userId:           selectedChat.userId || selectedChat.id,
                    userName:         selectedChat.userName || 'Customer',
                    userEmail:        selectedChat.userEmail || '',
                    status:           'active',
                    lastMessage:      '',
                    lastMessageBy:    null,
                    ticketId:         `TKT-${selectedChat.id.slice(0, 6).toUpperCase()}`,
                    unreadByAdmin:    0,
                    unreadByCustomer: 0,
                    createdAt:        serverTimestamp(),
                    updatedAt:        serverTimestamp(),
                    lastMessageTime:  serverTimestamp(),
                });
            }

            await addDoc(collection(db, 'chats', selectedChat.id, 'messages'), {
                senderId:        currentUser.uid,
                senderType:      'admin',
                senderName:      'JG Printing Admin',
                messageType:     'text',
                messageText:     messageText.trim(),
                imageUrl:        null,
                readByAdmin:     true,
                readByCustomer:  false,
                timestamp:       serverTimestamp(),
            });

            await updateDoc(chatRef, {
                lastMessage:      messageText.trim(),
                lastMessageTime:  serverTimestamp(),
                lastMessageBy:    'admin',
                unreadByCustomer: increment(1),
                updatedAt:        serverTimestamp(),
            });

            setTransientChat(null);
            setMessageText('');
        } catch (error) {
            console.error('Error sending message:', error);
            setActionError(getErrorMessage(error, 'Failed to send message. Please try again.'));
        } finally {
            setSending(false);
        }
    };

    const handleMarkAsResolved = async () => {
        if (!selectedChat) return;
        if (window.confirm('Mark this conversation as resolved?')) {
            try {
                await updateDoc(doc(db, 'chats', selectedChat.id), {
                    status: 'resolved', updatedAt: serverTimestamp()
                });
                setSelectedChat(null);
                setShowOptions(false);
            } catch (error) {
                console.error('Error marking as resolved:', error);
                setActionError(getErrorMessage(error, 'Failed to mark as resolved. Please try again.'));
            }
        }
    };

    const handleReopenChat = async () => {
        if (!selectedChat) return;
        if (window.confirm('Reopen this conversation?')) {
            try {
                await updateDoc(doc(db, 'chats', selectedChat.id), {
                    status: 'active', updatedAt: serverTimestamp()
                });
                setSelectedChat(null);
                setShowOptions(false);
            } catch (error) {
                console.error('Error reopening chat:', error);
                setActionError(getErrorMessage(error, 'Failed to reopen chat. Please try again.'));
            }
        }
    };

    const handleDeleteChat = async () => {
        if (!selectedChat) return;
        if (window.confirm('Delete this conversation? This cannot be undone.')) {
            try {
                // 1. Burahin muna lahat ng messages sa subcollection
                const messagesRef = collection(db, 'chats', selectedChat.id, 'messages');
                const messagesSnap = await getDocs(messagesRef);
                const deletePromises = messagesSnap.docs.map(d => deleteDoc(d.ref));
                await Promise.all(deletePromises);

                // 2. Pagkatapos, burahin na ang chat document mismo
                await deleteDoc(doc(db, 'chats', selectedChat.id));

                setSelectedChat(null);
                setShowOptions(false);
            } catch (error) {
                console.error('Error deleting chat:', error);
                setActionError(getErrorMessage(error, 'Failed to delete chat. Please try again.'));
            }
        }
    };

    const mergedChats = transientChat && !chats.some(c => c.id === transientChat.id || c.userId === transientChat.userId)
        ? [transientChat, ...chats]
        : chats;

    const filteredChats = mergedChats.filter(chat =>
        chat.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        chat.userEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        chat.lastMessage?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now   = new Date();
        const diff  = Math.floor((now - date) / 1000);
        if (diff < 60)     return 'Just now';
        if (diff < 3600)   return `${Math.floor(diff / 60)} min ago`;
        if (diff < 86400)  return `${Math.floor(diff / 3600)} hr ago`;
        if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
        return date.toLocaleDateString();
    };

    const formatMessageTime = (timestamp) => {
        if (!timestamp) return '';
        const d = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="admin-chats-container">
            {actionError && (
                <div className="admin-chats-banner admin-chats-banner--error" onClick={() => setActionError(null)}>
                    {actionError}
                </div>
            )}
            {/* ── Chat List Panel ── */}
            <div className={`admin-chats-list${selectedChat ? ' admin-chats-list--hidden-mobile' : ''}`}>
                <h2 className="admin-chats-list-title">All Messages</h2>

                <div className="admin-chats-tabs" style={{ display: 'flex', gap: '8px', padding: '0 24px 16px', borderBottom: '1px solid #eee' }}>
                    <button 
                        style={{ flex: 1, padding: '8px', border: 'none', background: activeTab === 'active' ? '#1a5faa' : '#f0f4f8', color: activeTab === 'active' ? '#fff' : '#1a5faa', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}
                        onClick={() => setActiveTab('active')}
                    >Active</button>
                    <button 
                        style={{ flex: 1, padding: '8px', border: 'none', background: activeTab === 'resolved' ? '#1a5faa' : '#f0f4f8', color: activeTab === 'resolved' ? '#fff' : '#1a5faa', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}
                        onClick={() => setActiveTab('resolved')}
                    >Resolved</button>
                </div>

                <div className="admin-chats-search">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                    </svg>
                    <input
                        type="text"
                        placeholder="Search messages"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                <div className="admin-chats-items">
                    {loading ? (
                        <div className="admin-chats-loading">Loading chats…</div>
                    ) : filteredChats.length === 0 ? (
                        <div className="admin-chats-empty">
                            {searchQuery ? 'No chats found' : 'No messages yet'}
                        </div>
                    ) : (
                        filteredChats.map((chat) => (
                            <button
                                key={chat.id}
                                className={`admin-chat-item ${selectedChat?.id === chat.id ? 'active' : ''}`}
                                onClick={() => handleSelectChat(chat)}
                            >
                                <div className="admin-chat-avatar">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm0 2c-4 0-7 2-7 4v2h14v-2c0-2-3-4-7-4z"/>
                                    </svg>
                                </div>
                                <div className="admin-chat-details">
                                    <div className="admin-chat-header">
                                        <span className="admin-chat-name">
                                            {chat.userName || chat.userEmail || 'User'}
                                            <span style={{ marginLeft: '8px', fontSize: '11px', background: '#e0e0e0', color: '#555', padding: '2px 6px', borderRadius: '12px', fontWeight: 'normal' }}>
                                                {chat.ticketId || `TKT-${chat.id.slice(0, 6).toUpperCase()}`}
                                            </span>
                                        </span>
                                        <span className="admin-chat-time">{formatTime(chat.lastMessageTime)}</span>
                                    </div>
                                    <div className="admin-chat-info">
                                        <span className="admin-chat-role">Customer</span>
                                        {chat.unreadByAdmin > 0 && (
                                            <span className="admin-chat-unread-badge">{chat.unreadByAdmin}</span>
                                        )}
                                    </div>
                                    <p className={`admin-chat-preview ${chat.unreadByAdmin > 0 ? 'unread' : ''}`}>
                                        {chat.lastMessage || 'No messages yet'}
                                    </p>
                                </div>
                            </button>
                        ))
                    )}
                </div>
            </div>

            {/* ── Chat Panel ── */}
            {selectedChat ? (
                <div className="admin-chat-panel admin-chat-panel--visible-mobile">
                    <div className="admin-chat-panel-header">
                        <div className="admin-chat-panel-user">
                            <button className="admin-chat-back-btn" onClick={() => setSelectedChat(null)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 12H5M5 12l7 7M5 12l7-7"/>
                                </svg>
                            </button>
                            <div className="admin-chat-avatar">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm0 2c-4 0-7 2-7 4v2h14v-2c0-2-3-4-7-4z"/>
                                </svg>
                            </div>
                            <div className="admin-chat-panel-info">
                                <span className="admin-chat-panel-name">
                                    {selectedChat.userName || selectedChat.userEmail || 'User'}
                                    <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '8px', background: '#e0e0e0', padding: '2px 6px', borderRadius: '12px' }}>
                                        {selectedChat.ticketId || `TKT-${selectedChat.id.slice(0, 6).toUpperCase()}`}
                                    </span>
                                </span>
                                <span className="admin-chat-panel-role">Customer</span>
                            </div>
                        </div>
                        <div className="admin-chat-panel-actions">
                            <button className="admin-chat-options-btn" onClick={() => setShowOptions(!showOptions)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/>
                                </svg>
                            </button>
                            {showOptions && (
                                <div className="admin-chat-options-menu">
                                    {activeTab === 'resolved' ? (
                                        <button onClick={handleReopenChat}>Reopen Chat</button>
                                    ) : (
                                        <button onClick={handleMarkAsResolved}>Mark as Resolved</button>
                                    )}
                                    <button onClick={handleDeleteChat} className="danger-text">Delete Chat</button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="admin-chat-messages">
                        {messages.length === 0 && (
                            <div className="admin-chat-no-messages">
                                No messages yet. Start the conversation!
                            </div>
                        )}
                        {messages.map((message) => (
                            <div
                                key={message.id}
                                className={`admin-message ${message.senderType === 'admin' ? 'sent' : 'received'}`}
                            >
                                {message.senderType === 'customer' && (
                                    <div className="admin-message-avatar">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm0 2c-4 0-7 2-7 4v2h14v-2c0-2-3-4-7-4z"/>
                                        </svg>
                                    </div>
                                )}
                                <div className="admin-message-content">
                                    <div className="admin-message-bubble">{message.messageText}</div>
                                    <span className="admin-message-ts">{formatMessageTime(message.timestamp)}</span>
                                </div>
                            </div>
                        ))}
                        {messages.length > 0 && messages[messages.length - 1].senderType === 'admin' && (
                            <div className="admin-message-status">Sent</div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                        {activeTab === 'resolved' && (
                            <div style={{ textAlign: 'center', padding: '16px', background: '#f9f9f9', borderTop: '1px solid #eee', color: '#666', fontSize: '13px' }}>
                                This chat is resolved. Reopen from the options menu to send new messages.
                            </div>
                        )}
                        {activeTab !== 'resolved' && (
                            <form className="admin-chat-input" onSubmit={handleSendMessage}>
                                <input
                                    type="text"
                                    placeholder="Type a message…"
                                    value={messageText}
                                    onChange={(e) => setMessageText(e.target.value)}
                                    disabled={sending}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSendMessage(e); } }}
                                />
                                <button type="submit" className="admin-send-btn" disabled={sending || !messageText.trim()}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="22" y1="2" x2="11" y2="13"/>
                                        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                                    </svg>
                                </button>
                            </form>
                        )}
                </div>
            ) : (
                <div className="admin-chat-empty-state">
                    <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M4 4h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-5 4v-4H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/>
                        <path d="M7 9h10M7 13h6"/>
                    </svg>
                    <h3>Select a conversation</h3>
                    <p>Choose a chat from the list to view messages</p>
                </div>
            )}
        </div>
    );
}

export default AdminChats;
